import { Bell, LogOut, User as UserIcon, ChevronDown } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Link, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

function initials(name?: string | null): string {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]![0] : '';
  return (first + last).toUpperCase() || '??';
}

function roleLabel(role: string | undefined): string {
  switch (role) {
    case 'PLATFORM_OWNER':
      return 'platform owner';
    case 'PLATFORM_SUPPORT':
      return 'platform support';
    case 'WORKSPACE_ADMIN':
      return 'admin';
    case 'OPERADOR':
      return 'operador';
    case 'CONTADOR_EXTERNO':
      return 'contador';
    default:
      return '—';
  }
}

export function UserChip({ className }: { className?: string }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;

  async function handleLogout() {
    try {
      await logout();
      toast.success('Você saiu da plataforma.');
      navigate({ to: '/login' });
    } catch {
      toast.error('Falha ao sair.');
    }
  }

  return (
    <DropdownMenu.Root>
      <div
        className={cn(
          'inline-flex h-[46px] items-center gap-3 rounded-pill border border-card-stroke bg-card-soft pl-1 pr-2 py-1',
          'shadow-pressed',
          className,
        )}
      >
        <button
          type="button"
          aria-label="Notificações"
          className="relative grid h-9 w-9 place-items-center rounded-full border border-card-stroke bg-frame transition-colors hover:bg-card"
        >
          <Bell className="h-3.5 w-3.5 text-fg" strokeWidth={1.8} />
          <span className="absolute right-1.5 top-1.5 h-[7px] w-[7px] rounded-full border-[1.5px] border-frame bg-block" />
        </button>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            aria-label="Abrir menu da conta"
            className={cn(
              'group flex items-center gap-2 pr-1 pl-2 h-[38px] rounded-full',
              'hover:bg-card transition-colors duration-fast',
              'data-[state=open]:bg-card',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint-deep focus-visible:ring-offset-1',
            )}
          >
            <span className="hidden md:inline text-[13px] font-medium text-fg">
              {user.name?.split(/\s+/)[0] ?? user.email}
            </span>
            <ChevronDown
              className="hidden md:block h-3.5 w-3.5 text-fg-muted transition-transform duration-fast group-data-[state=open]:rotate-180"
              strokeWidth={1.8}
            />
            <span
              className={cn(
                'grid h-[38px] w-[38px] place-items-center rounded-full',
                'bg-[linear-gradient(135deg,var(--clay-deep),#8E5736)]',
                'text-clay-soft font-mono text-[12px] font-semibold tracking-tight',
                'shadow-[inset_0_1px_0_rgba(255,255,255,0.20),inset_0_-2px_4px_rgba(0,0,0,0.18),0_4px_10px_-4px_rgba(184,120,80,0.35)]',
              )}
            >
              {initials(user.name)}
            </span>
          </button>
        </DropdownMenu.Trigger>
      </div>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className={cn(
            'z-50 w-[280px] rounded-3 border border-card-stroke bg-card shadow-clay p-1',
            'data-[state=open]:animate-fade-in',
          )}
        >
          {/* Header do usuário */}
          <div className="flex items-center gap-3 px-3 py-3 border-b border-card-stroke mb-1">
            <span
              className={cn(
                'grid h-10 w-10 shrink-0 place-items-center rounded-full',
                'bg-[linear-gradient(135deg,var(--clay-deep),#8E5736)]',
                'text-clay-soft font-mono text-[12px] font-semibold',
                'shadow-[inset_0_1px_0_rgba(255,255,255,0.20),inset_0_-2px_4px_rgba(0,0,0,0.18)]',
              )}
            >
              {initials(user.name)}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-fg truncate">{user.name ?? user.email}</p>
              <p className="text-[11px] text-fg-muted truncate font-mono">{user.email}</p>
              <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-mono uppercase tracking-[0.10em] text-mint-deep">
                <span className="h-1.5 w-1.5 rounded-full bg-mint-deep" />
                {roleLabel(user.role)}
              </span>
            </div>
          </div>

          <DropdownMenu.Item asChild>
            <Link
              to="/configuracoes"
              className={cn(
                'flex items-center gap-2.5 rounded-2 px-3 py-2 text-sm text-fg cursor-pointer outline-none',
                'data-[highlighted]:bg-card-soft data-[highlighted]:text-fg transition-colors',
              )}
            >
              <UserIcon className="h-4 w-4 text-fg-muted" strokeWidth={1.8} />
              Meu perfil
            </Link>
          </DropdownMenu.Item>

          <DropdownMenu.Separator className="my-1 h-px bg-card-stroke" />

          <DropdownMenu.Item
            onSelect={(e) => {
              e.preventDefault();
              void handleLogout();
            }}
            className={cn(
              'flex items-center gap-2.5 rounded-2 px-3 py-2 text-sm text-warn cursor-pointer outline-none',
              'data-[highlighted]:bg-warn-soft transition-colors',
            )}
          >
            <LogOut className="h-4 w-4" strokeWidth={1.8} />
            Sair
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
